import AddIcon from "@mui/icons-material/Add";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Button, Checkbox, Chip, Grid, IconButton } from "@mui/material";
import { format } from "date-fns";
import { uniqueId } from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { get, post, request } from "../../utils/io";
import { Protected } from "../Authz/Protected";
import MainContentContainer from "../Layouts/MainContentContainer";
import StelDataGrid from "../StelDataGrid";
import SalesOrdersSearchForm from "./components/SalesOrderSearchForm";
import CreateOrderModal from "components/StelModal/CreateOrderModal";

const emptySearchInputs = {
  so_ref: "",
  customer: "",
};

export const getStatusColor = (status) => {
  switch (status) {
    case "Cancelled":
      return { color: "#D32F2F", backgroundColor: "#FFF4F4" };
    case "In Progress":
      return { color: "#EFA700", backgroundColor: "#FFF9EA" };
    case "Pending":
      return { color: "#343434", backgroundColor: "#EBEBEB" };
    case "Complete":
      return { color: "#31883C", backgroundColor: "#E7FFEA" };
    case "Shipped":
      return { color: "#2FAFD3", backgroundColor: "#F4FFFF" };
    case "Returned":
      return { color: "#D32FC0", backgroundColor: "#FFF4FE" };
    case "Ready to Estimate":
      return { color: "#4E8AF9", backgroundColor: "#EEF5FF" };
    case "Estimate Sent":
      return { color: "#7439E2", backgroundColor: "#F5F2FF" };
    case "Delivery Confirmed":
      return { color: "#31887eff", backgroundColor: "#dfffefa4" };
    default:
      return { color: null, backgroundColor: null };
  }
};

function SalesOrders() {
  const [listDeviceRequestPrice, setListDeviceRequestPrice] = useState([]);
  const [searchFields, setSearchFields] = useState(emptySearchInputs);
  const [loading, setLoading] = useState(false);
  const [salesOrders, setSalesOrders] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loadingRequestPrice, setLoadingRequestPrice] = useState(false);
  const [loadingSendEstimateId, setLoadingSendEstimateId] = useState();
  const [isCreateSalesOrderOpen, setIsCreateSalesOrderOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(null);

  const selectedIds = useMemo(
    () => new Set(listDeviceRequestPrice.map((d) => d.line_id)),
    [listDeviceRequestPrice]
  );

  const getOrderList = useCallback(async () => {
    setLoading(true);

    const params = {
      so_ref: searchFields?.so_ref,
      customer: searchFields?.customer,
      status: searchFields?.status,
      offset: paginationModel.pageSize * paginationModel.page,
      limit: paginationModel.pageSize,
    };

    const res = await get("admin/sales_orders", params);

    if (res.status === 200) {
      setTotalCount(res.data.total_count);
      setSalesOrders(
        (res?.data?.sales_orders || []).map((item) => ({
          id: item.so_id,
          ...item,
        }))
      );
    } else {
      setSalesOrders([]);
    }

    setLoading(false);
  }, [searchFields, paginationModel]);

  const handleSelectDevice = useCallback(
    (device) => {
      setListDeviceRequestPrice((prev) => {
        const exists = prev.some((item) => item.line_id === device.line_id);

        if (exists) {
          return prev.filter((item) => item.line_id !== device.line_id);
        }

        const totalQuantity = salesOrders.reduce((sum, salesOrder) => {
          const matchingItems = salesOrder.items.filter(
            (item) =>
              item.line_id === device.line_id &&
              salesOrder.status !== "Cancelled"
          );
          return (
            sum + matchingItems.reduce((acc, item) => acc + item.quantity, 0)
          );
        }, 0);

        return [
          ...prev,
          {
            part_id: device.part_id,
            quantity: totalQuantity,
            line_id: device.line_id,
          },
        ];
      });
    },
    [salesOrders]
  );

  const handleSendEstimate = async (soId) => {
    setLoadingSendEstimateId(soId);
    const res = await post("admin/send_estimate", { so_id: soId });
    if (res.status === 200) {
      enqueueSnackbar({
        message: "Estimate sent successfully",
        variant: "success",
      });
      getOrderList();
      setLoadingSendEstimateId(null);
      return;
    }
    enqueueSnackbar({
      message: "Failed to send estimate",
      variant: "error",
    });
    setLoadingSendEstimateId(null);
  };

  const handleRequestPrice = async () => {
    setLoadingRequestPrice(true);
    const listDevice = Object.values(
      listDeviceRequestPrice.reduce((acc, { part_id: partId, quantity }) => {
        acc[partId] = {
          part_id: partId,
          quantity: (acc[partId]?.quantity || 0) + quantity,
        };
        return acc;
      }, {})
    );
    const res = await post("admin/request_price", { items: listDevice });
    if (res.status === 200) {
      enqueueSnackbar({
        message: "Price request sent successfully",
        variant: "success",
      });
      setLoadingRequestPrice(false);
      return;
    }
    enqueueSnackbar({
      message: "Failed to send price request",
      variant: "error",
    });
    setLoadingRequestPrice(false);
    setListDeviceRequestPrice([]);
  };

  const handleAttachmentUpload = async (soId, file) => {
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      enqueueSnackbar({
        message: "Only PDF files are allowed",
        variant: "error",
      });
      return;
    }

    setUploadingAttachment(soId);

    const formData = new FormData();
    formData.append("attachment", file);

    try {
      const res = await post(
        `admin/sales_orders/${soId}/upload_attachment`,
        formData
      );

      if (res && (res.status === 200 || res.status === 201)) {
        enqueueSnackbar({
          message: "Attachment uploaded successfully",
          variant: "success",
        });
        getOrderList();
      } else {
        const errorMessage = res?.data?.error || "Failed to upload attachment";
        enqueueSnackbar({
          message: errorMessage,
          variant: "error",
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || "Error uploading attachment";
      enqueueSnackbar({
        message: errorMessage,
        variant: "error",
      });
    }

    setUploadingAttachment(null);
  };

  const handleAttachmentDownload = async (attachmentId, filename) => {
    try {
      const res = await request(
        "get",
        `admin/sales_orders/attachments/${attachmentId}/download`,
        { responseType: "blob" }
      );

      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      enqueueSnackbar({
        message: "Attachment downloaded successfully",
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar({
        message: "Error downloading attachment",
        variant: "error",
      });
    }
  };

  useEffect(() => {
    getOrderList();
  }, [searchFields, paginationModel, getOrderList]);

  const columns = [
    {
      field: "so_ref",
      headerName: "Refrence",
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{params.value}</span>
          <IconButton
            size="small"
            sx={{ p: 0.5 }}
            onClick={() =>
              window.open(
                `https://inventory.stellife.com/order/sales-order/${params.row.so_id}/`,
                "_blank"
              )
            }
            title="Open in InvenTree"
          >
            <OpenInNewIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ),
    },
    {
      field: "items",
      headerName: "Item Name",
      flex: 2.5,
      cellClassName: "col-device",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {params?.row?.items?.map((device, idx) => (
            <Box
              key={uniqueId(device.line_id)}
              sx={{
                padding: 0,
                margin: 0,
                display: "flex",
                gap: 1,
                ...(idx > 0 && {
                  borderTop: "1px solid",
                  borderColor: "divider",
                }),
              }}
            >
              <Checkbox
                size="small"
                checked={
                  selectedIds.has(device.line_id) &&
                  params.row.status !== "Cancelled"
                }
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                onChange={() => handleSelectDevice(device)}
                disabled={params.row.status === "Cancelled"}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  mr: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: 14,
                    }}
                  >
                    {device.quantity}x
                  </Box>
                  <Box
                    sx={{
                      fontSize: 14,
                    }}
                  >
                    {device.name}
                  </Box>
                </Box>
                <Box
                  sx={{
                    fontWeight: "bold",
                    fontSize: 14,
                  }}
                >
                  {device?.price
                    ? `$${parseFloat(device?.price || 0).toFixed(2)}`
                    : "-"}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ),
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.2,
      renderCell: (params) => {
        const { status, status_custom_label: statusCustomLabel } = params.row;
        return (
          <Chip
            sx={{
              my: "4px",
              color: getStatusColor(statusCustomLabel || status).color,
              backgroundColor: getStatusColor(statusCustomLabel || status)
                .backgroundColor,
              borderColor: getStatusColor(statusCustomLabel || status).color,
            }}
            label={statusCustomLabel || status}
            variant="outlined"
            size="small"
          />
        );
      },
    },
    {
      field: "created_time",
      headerName: "Created Time",
      flex: 1,
      renderCell: (params) =>
        params?.row?.created_time
          ? format(new Date(params.row.created_time), "MM/dd/yyyy")
          : "N/A",
    },
    {
      field: "customer_name",
      headerName: "Customer",
      flex: 1,
    },
    {
      field: "total_quantity",
      headerName: "Quantity",
      flex: 0.6,
    },
    {
      field: "attachments",
      headerName: "Attachments",
      flex: 2,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            py: 1,
            gap: 0.5,
          }}
        >
          <input
            type="file"
            accept=".pdf"
            style={{ display: "none" }}
            id={`file-input-${params.row.id}`}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleAttachmentUpload(params.row.id, e.target.files[0]);
              }
            }}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor={`file-input-${params.row.id}`}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
              }}
            >
              <IconButton
                component="span"
                size="small"
                disabled={uploadingAttachment === params.row.id}
                sx={{ p: 0 }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Box sx={{ fontSize: 13 }}>Add attachment</Box>
            </Box>
          </label>
          {params.row.attachments && params.row.attachments.length > 0 && (
            <>
              {params.row.attachments.map((attachment) => (
                <Box
                  key={attachment.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() =>
                    handleAttachmentDownload(attachment.id, attachment.filename)
                  }
                >
                  <AttachFileIcon sx={{ fontSize: 16 }} />
                  <Box
                    sx={{
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {attachment.filename}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Box>
      ),
      sortable: false,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1.5,
      renderCell: (params) => {
        // Show "Download PO" button if PO is attached
        if (params.row.po_file_attached) {
          return (
            <Button
              sx={{ my: "4px" }}
              variant="outlined"
              size="small"
              onClick={async () => {
                try {
                  const response = await request(
                    "get",
                    `admin/sales_orders/${params.row.id}/download_po`,
                    { responseType: "blob" }
                  );

                  const blob = response.data;
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `PO_${
                    params.row.po_number || params.row.id
                  }.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);

                  enqueueSnackbar({
                    message: "PO downloaded successfully",
                    variant: "success",
                  });
                } catch (error) {
                  enqueueSnackbar({
                    message: "Error downloading PO",
                    variant: "error",
                  });
                }
              }}
            >
              Download PO
            </Button>
          );
        }

        // Show "Send Estimate" button if ready to estimate
        if (params.row.status_custom_label === "Ready to Estimate") {
          return (
            <Button
              loading={loadingSendEstimateId === params.row.so_id}
              sx={{ my: "4px" }}
              variant="contained"
              size="small"
              onClick={() => handleSendEstimate(params.row.so_id)}
            >
              Send Estimate
            </Button>
          );
        }

        return null;
      },
    },
  ];

  return (
    <MainContentContainer title="Sales Orders" isEnterpriseOnly>
      <Grid container direction="column">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <SalesOrdersSearchForm
            onSubmit={setSearchFields}
            isSearchDisabled={loading}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            sx={{ height: "fit-content", mb: "15px" }}
            color="primary"
            variant="contained"
            onClick={() => setIsCreateSalesOrderOpen(true)}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
          <Protected permission="RequestPrice">
            <Button
              loading={loadingRequestPrice}
              permission="RequestPrice"
              disabled={!listDeviceRequestPrice.length}
              sx={{ height: "fit-content", mb: "15px" }}
              color="primary"
              variant="contained"
              onClick={handleRequestPrice}
            >
              Request Price
            </Button>
          </Protected>
        </Box>

        <StelDataGrid
          rows={salesOrders}
          columns={columns}
          getRowHeight={() => "auto"}
          sx={{
            "& .MuiDataGrid-cell.col-device": {
              px: 0,
              py: 0,
              borderRight: "1px solid #0000003B",
              borderLeft: "1px solid #0000003B",
            },
          }}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          rowCount={totalCount}
        />
      </Grid>
      <CreateOrderModal
        open={isCreateSalesOrderOpen}
        onClose={() => setIsCreateSalesOrderOpen(false)}
        onSuccess={getOrderList}
        isSalesOrder
      />
    </MainContentContainer>
  );
}
export default SalesOrders;
